import { logger } from '@superbet-group/web.lib.utilities';

import {
    BetlerWelcomeBonus,
    WelcomeBonusKind,
    WelcomeBonusUsage,
} from '@core/models/bonus/WelcomeBonus';
import { WelcomeOfferBonusType } from '@core/models/bonus/WelcomeOfferBonusType';
import { WelcomeOfferBonusTypeDto } from '@core/models/bonus/WelcomeOfferBonusTypeDto';

import {
    BetlerWelcomeBonusDto,
    WelcomeBonusUsageDto,
} from './dtos/WelcomeBonusDto';

export function transformBetlerWelcomeBonus(dto: BetlerWelcomeBonusDto): BetlerWelcomeBonus {
    const { cardTitle, shortDescription, longDescription, bonusExplanation } = getWBStaticInfo(dto.type);
    return {
        kind: WelcomeBonusKind.Betler,
        id: dto.id,
        title: getTitle(dto.type),
        description: dto.description,
        image: getImagePath(dto.type),
        type: getWelcomeBonusType(dto.type),
        buttonText: getButtonText(dto.type),
        usage: dto.usage.map((usage) => transformUsageData(usage)),
        cardTitle,
        shortDescription,
        longDescription,
        bonusExplanation,
    };
}

function transformUsageData(usageDto: WelcomeBonusUsageDto): WelcomeBonusUsage {
    return {
        name: usageDto.name,
        award: usageDto.award,
        description: {
            title: usageDto.description.title,
            conditions: usageDto.description.conditions.filter(Boolean),
        },
    };
}

function getTitle(typeDto: WelcomeOfferBonusTypeDto) : WelcomeOfferBonusType {
    const title = titleMapping[typeDto];
    if (!title) {
        logger.error(new Error(`Undefined title for welcome bonus type: ${typeDto}`));
    }
    return title;
}

function getImagePath(typeDto: WelcomeOfferBonusTypeDto) : WelcomeOfferBonusType {
    const image = imgPathMapping[typeDto];
    if (!image) {
        logger.error(new Error(`Undefined image for welcome bonus type: ${typeDto}`));
    }
    return image;
}

function getWelcomeBonusType(typeDto: WelcomeOfferBonusTypeDto) : WelcomeOfferBonusType {
    const type = typeMappings[typeDto];
    if (!type) {
        logger.error(new Error(`Undefined type for welcome bonus type: ${typeDto}`));
    }
    return type;
}

function getButtonText(typeDto: WelcomeOfferBonusTypeDto) : WelcomeOfferBonusType {
    const buttonText = buttonTextMappings[typeDto];
    if (!buttonText) {
        logger.error(new Error(`Undefined button text for welcome bonus type: ${typeDto}`));
    }
    return buttonText;
}

function getWBStaticInfo(type: WelcomeOfferBonusTypeDto) {
    if (type === WelcomeOfferBonusTypeDto.Sport) {
        return {
            cardTitle: 'bonus.welcome_offer.sport_bonus_title',
            shortDescription: 'bonus.welcome_offer.short_description_sport_bonus',
            longDescription: 'bonus.welcome_offer.long_description_sport_bonus',
            bonusExplanation: 'bonus.welcome_offer.explanation_sport_bonus',
        };
    } else {
        return {
            cardTitle: 'bonus.welcome_offer.casino_bonus_title',
            shortDescription: 'bonus.welcome_offer.short_description_casino_bonus',
            longDescription: 'bonus.welcome_offer.long_description_casino_bonus',
            bonusExplanation: 'bonus.welcome_offer.explanation_casino_bonus',
        };
    }
}

const titleMapping: Record<WelcomeOfferBonusTypeDto, string> = {
    [WelcomeOfferBonusTypeDto.Sport]: 'bonus.overview.sport',
    [WelcomeOfferBonusTypeDto.Casino]: 'bonus.overview.casino',
};

const imgPathMapping: Record<WelcomeOfferBonusTypeDto, string> = {
    [WelcomeOfferBonusTypeDto.Sport]: '/static/img/bonus/welcome-bonus__sport.png',
    [WelcomeOfferBonusTypeDto.Casino]: '/static/img/bonus/welcome-bonus__casino.png',
};

const typeMappings: Record<WelcomeOfferBonusTypeDto, WelcomeOfferBonusType> = {
    [WelcomeOfferBonusTypeDto.Sport]: WelcomeOfferBonusType.Sport,
    [WelcomeOfferBonusTypeDto.Casino]: WelcomeOfferBonusType.Casino,
};

const buttonTextMappings: Record<WelcomeOfferBonusTypeDto, string> = {
    [WelcomeOfferBonusTypeDto.Sport]: 'bonus.welcome_offer.select_sport_bonus',
    [WelcomeOfferBonusTypeDto.Casino]: 'bonus.welcome_offer.select_casino_bonus',
};
